import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { ArrowDropDown, DeleteOutline, Edit } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { type MRT_ColumnDef } from 'material-react-table';
import { useFormContext } from 'react-hook-form';
import { LearningBudgetSetupItemDirtyDot } from '../LearningBudgetSetupItemDirtyDot';
import {
  CompanyRow,
  EmployeeRow,
  TeamRow,
} from '../LearningBudgetSetupsTable.types';

export type TableRow = CompanyRow | TeamRow | EmployeeRow;

export type ColumnsProps = {
  onEdit: (row: TableRow) => void;
  onDelete: (row: TableRow) => void;
};

export const createColumns = ({
  onEdit,
  onDelete,
}: ColumnsProps): MRT_ColumnDef<TableRow>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    Cell: ({ row: { original, toggleExpanded } }) => {
      if (original.variant === 'company') {
        return (
          <Box className="flex items-center gap-1">
            <Typography variant="subtitle2">
              Default Company Level Learning Budget:{' '}
              {formatCurrencyValue(
                original.budget.amount,
                original.budget.currency,
              )}
            </Typography>
            <LearningBudgetSetupItemDirtyDot isDirty={original.isDirty} />
          </Box>
        );
      }

      if (original.variant === 'team') {
        return (
          <div className="flex items-center gap-1">
            <IconButton
              size="small"
              color="inherit"
              disabled={!original.employeeOverrides.length}
              title={
                !original.employeeOverrides.length
                  ? 'No employees in this team'
                  : undefined
              }
              onClick={() => toggleExpanded()}
            >
              <ArrowDropDown />
            </IconButton>
            <Box className="flex items-center gap-1">
              <Typography variant="subtitle2">
                <span className="capitalize">{original.team.name}</span> team:{' '}
                {formatCurrencyValue(
                  original.budget.amount,
                  original.budget.currency,
                )}
              </Typography>
              <LearningBudgetSetupItemDirtyDot isDirty={original.isDirty} />
            </Box>
          </div>
        );
      }

      if (original.variant === 'employee') {
        return (
          <Box className="flex items-center gap-1">
            <Typography className="pl-2" variant="body2">
              {[original.employee.firstName, original.employee.lastName]
                .filter(Boolean)
                .join(' ')}
              :{' '}
              {formatCurrencyValue(
                original.budget.amount,
                original.budget.currency,
              )}
            </Typography>
            <LearningBudgetSetupItemDirtyDot isDirty={original.isDirty} />
          </Box>
        );
      }
    },
  },
  {
    header: 'Actions',
    muiTableHeadCellProps: {
      align: 'right',
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
    Cell: ({ row }) => {
      const {
        formState: { isSubmitting, disabled },
      } = useFormContext();

      return (
        <div className="flex gap-2">
          {['employee', 'team'].includes(row.original.variant) && (
            <Button
              size="small"
              variant="text"
              startIcon={<DeleteOutline />}
              className="bg-white"
              color="error"
              disabled={isSubmitting || disabled}
              onClick={() => onDelete(row.original)}
            >
              Delete
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            startIcon={<Edit />}
            className="bg-white"
            onClick={() => onEdit(row.original)}
            disabled={isSubmitting || disabled}
          >
            {row.original.variant === 'company' ? 'Set' : 'Edit'}
          </Button>
        </div>
      );
    },
  },
];
